import { cn } from '@noor/lib';
import { weekScheduleSchema, type Translate } from '@/entities/moderation';
import { ReviewSection } from './review-modal-shell';

const DAYS = [
  ['MONDAY', 'mon', 'Пн'],
  ['TUESDAY', 'tue', 'Вт'],
  ['WEDNESDAY', 'wed', 'Ср'],
  ['THURSDAY', 'thu', 'Чт'],
  ['FRIDAY', 'fri', 'Пт'],
  ['SATURDAY', 'sat', 'Сб'],
  ['SUNDAY', 'sun', 'Вс'],
] as const;

const TIME_PILL =
  'flex h-6 min-w-[73px] items-center justify-center rounded-full border border-border bg-card px-4 text-sm font-normal shadow-xs tabular-nums';

/** Slot schedules also accept payload data when the card is unavailable. */
export function WeekScheduleSection({
  schedule,
  liveSchedule,
  highlight = false,
  t,
}: {
  schedule: unknown;
  liveSchedule?: unknown;
  highlight?: boolean;
  t: Translate;
}) {
  const days = weekScheduleSchema.safeParse(schedule).data ?? [];
  const liveDays = weekScheduleSchema.safeParse(liveSchedule).data ?? [];

  return (
    <ReviewSection title={t('restaurantCreate.hoursSection', 'График работы')}>
      <dl className="flex flex-col gap-3">
        {DAYS.map(([code, key, fallback]) => {
          const day = days.find(
            (row) => (row.day_of_week ?? row.day)?.toUpperCase() === code,
          );
          const live = liveDays.find(
            (row) => (row.day_of_week ?? row.day)?.toUpperCase() === code,
          );
          const hoursChanged =
            highlight &&
            (Boolean(day?.is_closed) !== Boolean(live?.is_closed) ||
              (!day?.is_closed &&
                (day?.open_time !== live?.open_time ||
                  day?.close_time !== live?.close_time)));
          // The order-acceptance window is its own field: moving it must not
          // mark the branch's working hours as changed (and vice versa).
          const orderWindowChanged =
            highlight &&
            !day?.is_closed &&
            ((day?.order_from ?? '') !== (live?.order_from ?? '') ||
              (day?.order_to ?? '') !== (live?.order_to ?? ''));
          const changed = hoursChanged || orderWindowChanged;
          return (
            <div
              key={code}
              className="flex items-center gap-3 text-sm text-foreground"
              data-testid={`week-schedule-${key}`}
              data-changed={changed || undefined}
              data-hours-changed={hoursChanged || undefined}
              data-order-window-changed={orderWindowChanged || undefined}
            >
              <dt className={cn('w-6 shrink-0', hoursChanged && 'text-destructive')}>
                {t(`restaurantCreate.days.${key}`, fallback)}
              </dt>
              <dd className="flex flex-wrap items-center gap-3">
                <span aria-hidden>-</span>
                {day?.is_closed ? (
                  <span className={cn(hoursChanged && 'text-destructive')}>
                    {t('restaurantCreate.closed', 'Выходной')}
                  </span>
                ) : (
                  <>
                    <span className={cn(TIME_PILL, hoursChanged && 'text-destructive')}>
                      {day?.open_time === '' ? '—' : (day?.open_time ?? '—')}
                    </span>
                    <span className={cn(TIME_PILL, hoursChanged && 'text-destructive')}>
                      {day?.close_time === '' ? '—' : (day?.close_time ?? '—')}
                    </span>
                  </>
                )}
                {day && !day.is_closed && (
                  <span
                    className={cn(
                      'basis-full text-xs',
                      orderWindowChanged ? 'text-destructive' : 'text-muted-foreground',
                    )}
                  >
                    {t('restaurantCreate.orderWindow', 'Приём заказов')}:{' '}
                    {day.order_from || day.order_to
                      ? `${day.order_from === '' ? '—' : (day.order_from ?? '—')} – ${day.order_to === '' ? '—' : (day.order_to ?? '—')}`
                      : t('restaurantCreate.orderAllDay', 'Весь рабочий день')}
                  </span>
                )}
              </dd>
            </div>
          );
        })}
      </dl>
    </ReviewSection>
  );
}
